import {
    COMPLETE_MULTIPART_MUTATION,
    INIT_AVATARS_MULTIPART_MUTATION,
    INIT_HYPE_PRIZES_MULTIPART_MUTATION,
    INIT_SHIELDS_MULTIPART_MUTATION,
    INIT_WALLHALLA_MULTIPART_MUTATION,
    SIGN_UPLOAD_PART_MUTATION,
} from "@/graphql/mutations/uploadMutations";
import { ApolloClient } from "@apollo/client";
import SparkMD5 from "spark-md5";

const CHUNK_SIZE = 8 * 1024 * 1024; // 8 MiB
const WALLHALLA_CHUNK_SIZE = 64 * 1024 * 1024; // 64 MiB for large TIF files
type Part = { ETag: string; PartNumber: number };

async function md5HexOfBlob(blob: Blob): Promise<string> {
    const buf = await blob.arrayBuffer();
    return SparkMD5.ArrayBuffer.hash(buf);
}

export async function uploadZipMultipart(
    client: ApolloClient<any>,
    file: File,
    onProgress?: (uploadedBytes: number, totalBytes: number) => void,
    parallel = 4,
    type: "shields" | "avatars" | "hype_prizes" | "wallhalla" = "shields",
): Promise<{ key: string }> {
    const chunkSize = type === "wallhalla" ? WALLHALLA_CHUNK_SIZE : CHUNK_SIZE;

    // Pick the correct init mutation
    const initMutation =
        type === "shields"
            ? INIT_SHIELDS_MULTIPART_MUTATION
            : type === "avatars"
              ? INIT_AVATARS_MULTIPART_MUTATION
              : type === "wallhalla"
                ? INIT_WALLHALLA_MULTIPART_MUTATION
                : INIT_HYPE_PRIZES_MULTIPART_MUTATION;

    const { data: init } = await client.mutate({
        mutation: initMutation,
        variables: {
            filename: file.name,
            contentType: file.type || "application/octet-stream",
        },
    });
    const { key, uploadId } =
        type === "shields"
            ? init.initShieldsMultipartUpload
            : type === "avatars"
              ? init.initAvatarsMultipartUpload
              : type === "wallhalla"
                ? init.initWallhallaMultipartUpload
                : init.initHypePrizesMultipartUpload;

    const totalParts = Math.ceil(file.size / chunkSize);
    let nextPart = 1;
    let uploadedBytes = 0;
    const parts: Part[] = [];

    async function uploadPart(partNumber: number) {
        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const blob = file.slice(start, end);

        const { data: sign } = await client.mutate({
            mutation: SIGN_UPLOAD_PART_MUTATION,
            variables: { key, uploadId, partNumber },
        });
        const url: string = sign.signUploadPart.url;

        const resp = await fetch(url, { method: "PUT", body: blob });
        if (!resp.ok)
            throw new Error(`Part ${partNumber} failed: ${resp.status}`);

        const etag = await md5HexOfBlob(blob);
        parts.push({ ETag: etag, PartNumber: partNumber });

        uploadedBytes += blob.size;
        onProgress?.(uploadedBytes, file.size);
    }

    const workers = Array.from({ length: Math.min(parallel, totalParts) }).map(
        async () => {
            while (true) {
                const current = nextPart++;
                if (current > totalParts) break;
                await uploadPart(current);
            }
        },
    );
    await Promise.all(workers);

    parts.sort((a, b) => a.PartNumber - b.PartNumber);
    await client.mutate({
        mutation: COMPLETE_MULTIPART_MUTATION,
        variables: { key, uploadId, parts },
    });

    return { key };
}
